import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import useChangeDraft from '@/features/dashboard/bookingChanges/hooks/useChangeDraft.ts';
import useCancelDraft from '@/features/dashboard/bookingChanges/hooks/useCancelDraft.ts';
import DraftCard from './DraftCard';
import DraftCardSkeleton from './DraftCardSkeleton';
import DraftImpactCard from './DraftImpactCard';
import DraftImpactCardSkeleton from './DraftImpactCardSkeleton';
import {
  pluralizeItems,
  pluralizeItemsAccusative,
} from '@/features/dashboard/bookingChanges/utils/plurals.ts';

interface DraftSectionProps {
  bookingId: string;
  /**
   * Ile pozycji szkicu dotyczy tego ekranu. Podane — karta szkicu mówi
   * „5 · w tym 3 tutaj”; pominięte (ekran rezerwacji) — samo „5 pozycji”.
   */
  localDraftItemCount?: number;
  onExitEditing: () => void;
}

/**
 * Karty szkicu — panel boczny w trybie edycji. Znikają, gdy szkicu nie ma,
 * tak samo jak belka szkicu w nagłówku.
 */
const DraftSection = ({
  bookingId,
  localDraftItemCount,
  onExitEditing,
}: DraftSectionProps) => {
  const navigate = useNavigate();
  const { confirmDeleteWithCallback } = confirmDialogUseHook();
  const { draft, isLoading, previewError } = useChangeDraft(bookingId);
  const cancelDraft = useCancelDraft(bookingId);

  if (isLoading) {
    return (
      <>
        <DraftCardSkeleton />
        <DraftImpactCardSkeleton />
      </>
    );
  }

  if (!draft) {
    return null;
  }

  const itemsLabel =
    localDraftItemCount === undefined
      ? pluralizeItems(draft.itemCount)
      : `${draft.itemCount} · w tym ${localDraftItemCount} tutaj`;

  const applyPackage = () =>
    navigate({
      to: '/app/bookings/$bookingId/changes/$changeId',
      params: { bookingId, changeId: draft.id },
      search: { step: ChangePackageStep.SUMMARY },
    });

  /*
   * Odrzucenie szkicu jest nieodwracalne i nie ma własnego modala w projekcie —
   * idzie więc przez globalny dialog potwierdzenia, tak jak reszta destrukcyjnych
   * akcji w aplikacji.
   */
  const discardDraft = () =>
    confirmDeleteWithCallback({
      title: 'Odrzucić szkic paczki?',
      description: `Szkic zawiera ${pluralizeItemsAccusative(draft.itemCount)}. Po odrzuceniu nie da się go przywrócić.`,
      confirmText: 'Odrzuć szkic',
      loadingText: 'Odrzucanie…',
      onConfirm: async () => {
        await cancelDraft.mutateAsync(draft.id);
        toast.success('Szkic zmian został odrzucony');
        onExitEditing();
      },
    });

  return (
    <>
      <DraftCard
        draft={draft}
        itemsLabel={itemsLabel}
        onApplyPackage={applyPackage}
        onSaveAndExit={onExitEditing}
        onDiscardDraft={discardDraft}
      />
      <DraftImpactCard draft={draft} amountsError={previewError} />
    </>
  );
};

export default DraftSection;
